import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DRAFT_REVIEW_ACTION_LABELS_ES,
  draftReviewStatusBadgeClass,
  formatDraftReviewStatus,
} from "../../../constants/draftListingReviewConstants";
import {
  fetchAdminListingDraftReview,
  updateAdminListingDraftReview,
} from "../../../services/automapDealerInventoryApi";

export default function DraftListingReviewPanel({ listingId, canManage, listing }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [message, setMessage] = useState("");

  const load = () => {
    setLoading(true);
    fetchAdminListingDraftReview(listingId)
      .then((data) => setReview(data.review))
      .catch(() => setReview(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [listingId]);

  const runAction = async (action, extra = {}) => {
    if (!canManage) return;
    setBusy(true);
    setMessage("");
    try {
      const data = await updateAdminListingDraftReview(listingId, { action, ...extra });
      setReview(data.review);
      setNoteDraft("");
      setMessage("Revisión actualizada.");
    } catch (err) {
      setMessage(err.message || "No pudimos actualizar la revisión.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <section className="admin-dashboard-card">
        <h3>Revisión del borrador</h3>
        <p className="admin-table-subtext">Cargando revisión…</p>
      </section>
    );
  }

  if (!review) return null;

  const { quality, publishGate, reviewStatuses } = review;
  const showPanel =
    listing?.sourceType === "seller_intake" ||
    listing?.sourceSubmissionId ||
    listing?.status === "draft";

  if (!showPanel) return null;

  return (
    <section className="admin-dashboard-card">
      <h3>Revisión del borrador</h3>
      <p className="admin-table-subtext">Origen: solicitud de vendedor</p>

      {review.sellerSubmissionAdminUrl ? (
        <p>
          <Link to={review.sellerSubmissionAdminUrl}>Ver solicitud de vendedor</Link>
        </p>
      ) : null}

      <p>
        <span className={draftReviewStatusBadgeClass(reviewStatuses.draftReviewStatus)}>
          {formatDraftReviewStatus(reviewStatuses.draftReviewStatus)}
        </span>{" "}
        <span className="lead-badge">{formatDraftReviewStatus(reviewStatuses.publishReadinessStatus)}</span>
      </p>

      <h4>Checklist</h4>
      <ul>
        {quality.checklist.map((item) => (
          <li key={item.id}>
            {item.passed ? "✓" : "○"} {item.label}
            {item.detail ? ` — ${item.detail}` : ""}
          </li>
        ))}
      </ul>

      {quality.blockers?.length ? (
        <>
          <h4>Bloqueadores</h4>
          <ul>
            {quality.blockers.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      ) : null}

      {quality.warnings?.length ? (
        <>
          <h4>Advertencias</h4>
          <ul>
            {quality.warnings.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      ) : null}

      <p className="admin-table-subtext">{quality.priceReview?.marketComparison}</p>

      {review.normalizationSuggestion?.uncertain ? (
        <p className="admin-table-subtext">
          Normalización sugerida: {review.normalizationSuggestion.normalizedMake}{" "}
          {review.normalizationSuggestion.normalizedModel}
        </p>
      ) : null}

      {canManage ? (
        <div className="dealer-inventory-actions" style={{ flexWrap: "wrap", marginTop: "1rem" }}>
          {[
            "mark_data_reviewed",
            "mark_price_reviewed",
            "mark_location_reviewed",
            "mark_normalization_reviewed",
            "apply_normalization_suggestion",
            "mark_photos_reviewed",
            "mark_ready_to_publish",
            "mark_blocked",
          ].map((action) => (
            <button
              key={action}
              type="button"
              disabled={busy || (action === "mark_ready_to_publish" && !publishGate.ok)}
              title={
                action === "mark_ready_to_publish" && !publishGate.ok
                  ? "Resuelve los bloqueadores antes de marcar listo para publicar."
                  : undefined
              }
              onClick={() => runAction(action)}
            >
              {DRAFT_REVIEW_ACTION_LABELS_ES[action]}
            </button>
          ))}
        </div>
      ) : null}

      {canManage ? (
        <div style={{ marginTop: "1rem" }}>
          <textarea
            rows={3}
            placeholder="Nota de revisión interna"
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
          />
          <button type="button" disabled={busy || !noteDraft.trim()} onClick={() => runAction("add_review_note", { reviewNote: noteDraft })}>
            {DRAFT_REVIEW_ACTION_LABELS_ES.add_review_note}
          </button>
        </div>
      ) : null}

      {review.reviewNotes ? (
        <>
          <h4>Notas de revisión</h4>
          <pre className="admin-table-subtext" style={{ whiteSpace: "pre-wrap" }}>
            {review.reviewNotes}
          </pre>
        </>
      ) : null}

      {message ? <p className="admin-table-subtext">{message}</p> : null}

      {!publishGate.ok ? (
        <p className="admin-table-subtext">
          La publicación permanece bloqueada hasta completar la revisión del borrador.
        </p>
      ) : null}
    </section>
  );
}
